import { TZ_SYNCED_ROW_BG, TZ_SYNCED_ROW_BORDER } from './tzConstructionSync';
import { getStageColor, hexToRgba } from './stageColors';

export const isSectionRow = (schedule) => {
  const num = (schedule.construction_stage || '').trim().replace(/\.+$/, '');
  const name = (schedule.work_name || '').trim();
  const stage = (schedule.sections || '').trim();
  if (!name && !stage) return false;
  if (num && !num.includes('.') && /^\d+$/.test(num)) return true;
  if (!num && !stage && name === name.toUpperCase() && /[A-ZА-ЯЁ]/.test(name)) return true;
  return false;
};

export const isSubsectionRow = (schedule) => {
  const num = (schedule.construction_stage || '').trim();
  if (!num || !/^\d/.test(num)) return false;
  const cleaned = num.replace(/\.+$/, '');
  const parts = cleaned.split('.').filter(Boolean);
  return parts.length === 2 && schedule.sections && schedule.work_name;
};

/** Нормализация № п/п: убрать хвостовые точки и пробелы (`2.1.` → `2.1`). */
export const normalizeOutlineNumber = (num) => {
  if (num == null) return null;
  const cleaned = String(num).trim().replace(/\.+$/, '');
  return cleaned || null;
};

export const getOutlineLevel = (num) => {
  if (num == null) return -1;
  const cleaned = String(num).replace(/\.+$/, '').trim();
  if (!cleaned || !/^\d/.test(cleaned)) return -1;
  const parts = cleaned.split('.').filter(Boolean);
  return Math.max(0, parts.length - 1);
};

export const getTopLevelChapterKey = (construction_stage) => {
  const cleaned = String(construction_stage || '').trim().replace(/\.+$/, '');
  if (!cleaned || !/^\d/.test(cleaned)) return null;
  const parts = cleaned.split('.').filter(Boolean);
  return parts[0] || null;
};

/** Ключ главы для строки (свой номер или унаследованный от строк выше). */
export const resolveChapterKeyForRow = (schedule, rowIndex, rows) => {
  const own = getTopLevelChapterKey(schedule?.construction_stage);
  if (own) return own;
  if (rowIndex == null || !rows?.length) return null;
  for (let i = rowIndex - 1; i >= 0; i--) {
    const key = getTopLevelChapterKey(rows[i]?.construction_stage);
    if (key) return key;
  }
  return null;
};

/** Порядок появления глав → индекс цвета в палитре. */
export const buildChapterColorMap = (rows) => {
  const map = new Map();
  let idx = 0;
  rows.forEach((row, rowIndex) => {
    const key = resolveChapterKeyForRow(row, rowIndex, rows);
    if (key && !map.has(key)) {
      map.set(key, idx);
      idx += 1;
    }
  });
  return map;
};

export const getChapterColorForRow = (schedule, rowIndex, rows, chapterColorMap) => {
  if (!chapterColorMap?.size) return null;
  const key = resolveChapterKeyForRow(schedule, rowIndex, rows);
  if (!key || !chapterColorMap.has(key)) return null;
  return getStageColor(chapterColorMap.get(key));
};

export const buildOutlineGroups = (rows) => {
  const levels = rows.map((r) => getOutlineLevel(r.construction_stage));
  const groups = new Map();
  for (let i = 0; i < rows.length; i++) {
    const L = levels[i];
    if (L < 0) continue;
    let j = i + 1;
    while (j < rows.length && levels[j] > L) j++;
    if (j > i + 1) groups.set(i, { start: i + 1, end: j - 1 });
  }
  return { groups, levels };
};

export const isRowHiddenByOutline = (rowIndex, groups, collapsedParentIds, rows) => {
  for (const [parentIdx, { start, end }] of groups) {
    const parentId = rows[parentIdx]?.id;
    if (!parentId || !collapsedParentIds.has(String(parentId))) continue;
    if (rowIndex >= start && rowIndex <= end) return true;
  }
  return false;
};

/**
 * Стили строки: иерархия, синхронизация, опционально цвет главы (как этапы в стройке).
 */
export const getHierarchyRowStyle = (schedule, options = {}) => {
  const {
    isNew = false,
    isSynced = false,
    isTzTable = false,
    colorizeByChapter = false,
    chapterColorMap = null,
    rowIndex = null,
    rows = null,
  } = options;

  const isSection = isSectionRow(schedule);
  const isSubsection = isSubsectionRow(schedule);
  const level = getOutlineLevel(schedule.construction_stage);

  const style = { transition: 'background-color 0.3s' };

  if (isSynced) {
    style.backgroundColor = TZ_SYNCED_ROW_BG;
    style.boxShadow = `inset ${TZ_SYNCED_ROW_BORDER}`;
  } else if (isNew) {
    style.backgroundColor = isTzTable ? '#f3e8ff' : '#e8f5e9';
  }

  const chapterColor =
    colorizeByChapter && !isSynced && !isNew
      ? getChapterColorForRow(schedule, rowIndex, rows, chapterColorMap)
      : null;

  if (chapterColor) {
    if (isSection) {
      style.backgroundColor = chapterColor.bg;
      style.color = chapterColor.text;
      style.fontWeight = 700;
      style.boxShadow = `inset 4px 0 0 ${chapterColor.border}`;
    } else if (isSubsection) {
      style.backgroundColor = hexToRgba(chapterColor.bg, 0.72);
      style.color = chapterColor.text;
      style.fontWeight = 600;
      style.boxShadow = `inset 3px 0 0 ${chapterColor.border}`;
    } else if (level >= 2) {
      style.backgroundColor = hexToRgba(chapterColor.bg, 0.28);
      style.boxShadow = `inset 2px 0 0 ${chapterColor.border}`;
    } else if (level === 1) {
      style.backgroundColor = hexToRgba(chapterColor.bg, 0.4);
      style.boxShadow = `inset 2px 0 0 ${chapterColor.border}`;
    } else {
      style.backgroundColor = hexToRgba(chapterColor.bg, 0.15);
      style.boxShadow = `inset 2px 0 0 ${chapterColor.border}`;
    }
  } else if (!isSynced && !isNew) {
    if (isSection) {
      style.backgroundColor = style.backgroundColor || 'var(--table-stripe)';
      style.fontWeight = 700;
    } else if (isSubsection && !isSection) {
      style.backgroundColor = style.backgroundColor || 'rgba(0, 123, 255, 0.06)';
      style.fontWeight = 600;
    }
  }

  return style;
};

export const getHierarchyCellFontWeight = (schedule) => {
  if (isSectionRow(schedule)) return 700;
  if (isSubsectionRow(schedule)) return 600;
  return 400;
};

/** Цвет полоски в колонке сворачивания outline. */
export const getOutlineIndentBorderColor = (schedule, rowIndex, rows, chapterColorMap, colorizeByChapter) => {
  if (!colorizeByChapter) return '#cbd5e1';
  const chapterColor = getChapterColorForRow(schedule, rowIndex, rows, chapterColorMap);
  return chapterColor?.border || '#cbd5e1';
};
